import { memo, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import TuneIcon from "@mui/icons-material/Tune";
import PaymentMethodLimitsForm, * as PaymentMethodLimits from "ui/forms/Merchant/PaymentMethodLimits";
import { FormAdapter } from "hooks/useForm";
import { convertObjectToNumberValues } from "util/transformers";
import useMerchantPaymentMethods from "./useMerchantPaymentMethods";

type Props = {
    selectedIds: Set<string>;
    onClear: () => void;
};

// Only these are applied in bulk. The template method's identity (methodId, tag, PSP, name...)
// must never be sent or merged into the other selected methods.
const limitFields: Array<keyof PaymentMethodLimits.PaymentMethodLimits> = [
    PaymentMethodLimits.FormField.MinimumTicketSize,
    PaymentMethodLimits.FormField.MaximumTicketSize,
    PaymentMethodLimits.FormField.MaxTransactionNumberWithin24HoursPerMerchant,
    PaymentMethodLimits.FormField.MaxTransactionNumberWithinWeekPerMerchant,
    PaymentMethodLimits.FormField.MaxTransactionNumberWithinMounthPerMerchant,
    PaymentMethodLimits.FormField.MaxTotalAmountWithinWeekPerMerchant,
    PaymentMethodLimits.FormField.MaxTransactionNumberIn24HoursForSamePersonTag,
    PaymentMethodLimits.FormField.AdjustMaxNumberOfTransactionProfile24ByConsumerRisk,
    PaymentMethodLimits.FormField.MaxTotalAmountIn24HoursForSamePersonTag,
    PaymentMethodLimits.FormField.AdjustMaxTotalAmountProfile24ByConsumerRisk,
    PaymentMethodLimits.FormField.MaxTotalAmountForSameProfileTag,
    PaymentMethodLimits.FormField.AdjustMaxTotalAmountMonthByConsumerRisk,
    PaymentMethodLimits.FormField.VelocitySec,
    PaymentMethodLimits.FormField.AdjustVelocityByConsumerRisk,
    PaymentMethodLimits.FormField.EmailRiskLevel,
    PaymentMethodLimits.FormField.EnableUserVerificationPage,
    PaymentMethodLimits.FormField.EnableMethodLimits
];

const pickLimits = (source?: Partial<PaymentMethodLimits.PaymentMethodLimits>) =>
    Object.fromEntries(
        limitFields
            .filter(field => source && field in source)
            .map(field => [field, source![field]])
    ) as PaymentMethodLimits.PaymentMethodLimits;

const BulkEditPaymentMethodLimits = ({ selectedIds, onClear }: Props) => {
    const [open, setOpen] = useState(false);
    const { repository } = useMerchantPaymentMethods();
    const selectedMethods = useMemo(() => repository.merchantsMethods.filter(method =>
        selectedIds.has(String(method.coreId)) && Boolean(method.paymentMethodIsAssignable)
    ), [repository.merchantsMethods, selectedIds]);
    const template = selectedMethods[0];
    const selectedMethodNames = selectedMethods.map(method =>
        `${method.paymentMethodTag} · ${method.paymentMethodPSP}`
    );

    const form = PaymentMethodLimits.useForm({
        initialValues: PaymentMethodLimits.getInitialValues(pickLimits(template)),
        onSaveOrCreate: async values => {
            const limits = pickLimits(values);
            let lastResponse: Awaited<ReturnType<typeof repository.updateMethodLimits>> | undefined;
            for (const method of selectedMethods) {
                // Same payload as the single-method limits form: the method's own pivot
                // (core-api validates its MOR/tag/PSP) with only the limit fields overridden.
                lastResponse = await repository.updateMethodLimits({
                    ...convertObjectToNumberValues({
                        ...PaymentMethodLimits.getInitialValues(method),
                        ...limits
                    }),
                    coreId: method.coreId
                });
            }
            setOpen(false);
            onClear();
            return FormAdapter.fromResponse(lastResponse!);
        }
    });

    return (
        <>
            <Button
                variant='outlined'
                startIcon={<TuneIcon />}
                disabled={!selectedMethods.length}
                onClick={() => setOpen(true)}
            >
                Edit limits
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='lg'>
                <DialogTitle>
                    Bulk edit limits · {selectedMethods.length} methods
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                        {selectedMethodNames.join(', ')}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Alert severity='info' sx={{ mb: 2 }}>
                        Values start from the first selected method. Saving applies every field below to all selected methods.
                    </Alert>
                    {template && (
                        <PaymentMethodLimitsForm
                            {...template}
                            {...form}
                            onCancel={() => setOpen(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default memo(BulkEditPaymentMethodLimits);
